import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StudentOverviewDetail from "@/components/StudentOverviewDetail";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, Search, Users, Clock, ListChecks, AlertTriangle, Award, Star, CalendarCheck, Briefcase } from "lucide-react";

export const STATUS_META = {
  none: { label: "بدون طلب", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  pending: { label: "قيد الانتظار", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  under_review: { label: "قيد المراجعة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "مقبول", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  assigned: { label: "مُسكَّن", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "مرفوض", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
export const CERT_META = {
  excellence: { label: "شهادة امتياز", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  completed: { label: "حصل على الشهادة", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  eligible: { label: "مستحق", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  not_eligible: { label: "غير مستوفٍ", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};
export const fmt = (v, suffix = "") => (v === null || v === undefined ? "—" : `${v}${suffix}`);

export function Badge({ meta, testId }) {
  return <span data-testid={testId} className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${meta?.cls || ""}`}>{meta?.label || "—"}</span>;
}

export function CertReasons({ r, testId }) {
  if (!r.certificate_reasons?.length) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-[10px] leading-4 text-rose-700 arabic-numerals" data-testid={testId}>
      {r.certificate_reasons.map((x) => <li key={x}>• {x}</li>)}
    </ul>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-cream-200 p-4 card-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-bold text-slate-500"><Icon className="h-4 w-4 text-teal-700" />{label}</div>
      <div className="mt-2 text-2xl font-heading font-black text-slate-900 arabic-numerals">{value}</div>
    </div>
  );
}

function SummaryCards({ s }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="overview-summary">
      <Stat icon={Users} label="عدد الطلاب" value={s.students} testId="stat-students" />
      <Stat icon={Briefcase} label="تسكين نشط" value={s.active_placements} testId="stat-active" />
      <Stat icon={Star} label="متوسط تقييم الجمعية" value={s.avg_association_score == null ? "—" : `${s.avg_association_score} من 100`} testId="stat-avg-score" />
      <Stat icon={Clock} label="إجمالي الساعات" value={s.total_hours} testId="stat-hours" />
      <Stat icon={ListChecks} label="مهام منجزة" value={s.tasks_done} testId="stat-tasks-done" />
      <Stat icon={AlertTriangle} label="مهام متأخرة" value={s.tasks_overdue} testId="stat-tasks-overdue" />
      <Stat icon={CalendarCheck} label="متوسط نسبة الحضور" value={fmt(s.avg_attendance_rate, "%")} testId="stat-attendance" />
      <Stat icon={Award} label="حاصلون على شهادة" value={s.certified} testId="stat-certified" />
    </div>
  );
}

function Filters({ f, setF, associations }) {
  const sel = "min-h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm";
  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4" data-testid="overview-filters">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="ابحث باسم الطالب أو البريد أو الفرصة…" className="w-full min-h-11 rounded-xl border border-slate-300 pr-9 pl-3 text-sm" data-testid="overview-search" />
      </div>
      <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={sel} data-testid="filter-status">
        <option value="">كل الحالات</option>
        {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
      </select>
      <select value={f.association} onChange={(e) => setF({ ...f, association: e.target.value })} className={sel} data-testid="filter-association">
        <option value="">كل الجمعيات</option>
        {associations.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </select>
      <select value={f.cert} onChange={(e) => setF({ ...f, cert: e.target.value })} className={sel} data-testid="filter-certificate">
        <option value="">كل حالات الشهادة</option>
        {Object.entries(CERT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
      </select>
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 min-h-11 px-2">
        <input type="checkbox" checked={f.overdue} onChange={(e) => setF({ ...f, overdue: e.target.checked })} className="h-4 w-4" data-testid="filter-overdue" />
        لديهم مهام متأخرة
      </label>
    </div>
  );
}

function useFiltered(rows, f) {
  return useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return rows.filter((r) =>
      (!f.status || r.status === f.status) &&
      (!f.association || r.association_id === f.association) &&
      (!f.cert || r.certificate_status === f.cert) &&
      (!f.overdue || r.tasks_overdue > 0) &&
      (!q || [r.student_name, r.student_email, r.opportunity_title, r.association_name, r.university].some((v) => (v || "").toLowerCase().includes(q))));
  }, [rows, f]);
}

export default function StudentsOverview() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [f, setF] = useState({ q: "", status: "", association: "", cert: "", overdue: false });
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/students-overview").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  const associations = useMemo(() => {
    const m = new Map();
    data.rows.forEach((r) => { if (r.association_id) m.set(r.association_id, r.association_name || "—"); });
    return [...m.entries()];
  }, [data.rows]);
  const rows = useFiltered(data.rows, f);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/students-overview/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "students-overview.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setExporting(false); }
  };

  return (
    <DashboardLayout>
      <div data-testid="students-overview">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">متابعة الأداء</div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-heading font-black text-slate-900">لوحة أداء الطلاب</h1>
            <p className="mt-2 text-slate-600 max-w-3xl text-sm">الدرجات وتقييم الجمعية والمهام والساعات والحضور وحالة الشهادة لكل طالب في مكان واحد.</p>
          </div>
          <button type="button" onClick={exportXlsx} disabled={exporting} className="inline-flex items-center justify-center gap-2 min-h-11 px-4 rounded-xl bg-teal-700 text-white text-sm font-bold hover:bg-teal-600 disabled:opacity-50" data-testid="export-xlsx-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير Excel
          </button>
        </div>
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : (
          <>
            <SummaryCards s={data.summary} />
            <Filters f={f} setF={setF} associations={associations} />
            <div className="text-xs text-slate-500 mb-2 arabic-numerals" data-testid="overview-count">{rows.length} نتيجة</div>
            <OverviewTable rows={rows} onOpen={setOpen} />
          </>
        )}
        {open && <StudentOverviewDetail row={open} onClose={() => setOpen(null)} />}
      </div>
    </DashboardLayout>
  );
}

function OverviewTable({ rows, onOpen }) {
  if (!rows.length) return <div className="p-8 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500 text-sm" data-testid="overview-empty">لا توجد نتائج مطابقة.</div>;
  const key = (r) => r.application_id || r.student_id;
  return (
    <>
      <div className="hidden lg:block bg-white rounded-2xl border border-cream-200 card-shadow overflow-x-auto">
        <table className="w-full text-sm" data-testid="overview-table">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>{["الطالب", "الفرصة / الجمعية", "الحالة", "تقييم الجمعية", "شهري (جمعية/موجّه)", "المهام", "متأخرة", "الساعات", "الحضور", "الشهادة"].map((h) => <th key={h} className="text-right font-bold px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="arabic-numerals">
            {rows.map((r) => (
              <tr key={key(r)} onClick={() => onOpen(r)} className="border-t border-slate-100 hover:bg-teal-50/40 cursor-pointer" data-testid={`overview-row-${key(r)}`}>
                <td className="px-3 py-3"><div className="font-bold text-slate-900">{r.student_name}</div><div className="text-xs text-slate-500">{r.university || "—"}</div></td>
                <td className="px-3 py-3 max-w-[220px]"><div className="truncate font-semibold text-teal-800">{r.opportunity_title || "—"}</div><div className="truncate text-xs text-slate-500">{r.association_name || "—"}</div></td>
                <td className="px-3 py-3"><Badge meta={STATUS_META[r.status]} /></td>
                <td className="px-3 py-3 font-black">{fmt(r.association_score, "/100")}</td>
                <td className="px-3 py-3">{fmt(r.monthly_association_avg)} / {fmt(r.monthly_mentor_avg)}</td>
                <td className="px-3 py-3">{r.tasks_done}/{r.tasks_total}</td>
                <td className={`px-3 py-3 font-bold ${r.tasks_overdue ? "text-rose-700" : "text-slate-400"}`}>{r.tasks_overdue}</td>
                <td className="px-3 py-3">{r.hours}</td>
                <td className="px-3 py-3">{fmt(r.attendance_rate, "%")}<div className="text-[10px] text-slate-500">{r.present_days} حضور · {r.absent_days} غياب</div></td>
                <td className="px-3 py-3 min-w-[170px]"><Badge meta={CERT_META[r.certificate_status]} /><CertReasons r={r} testId={`cert-reasons-${key(r)}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden grid gap-3 sm:grid-cols-2">
        {rows.map((r) => <MobileCard key={key(r)} r={r} onOpen={onOpen} />)}
      </div>
    </>
  );
}

function MobileCard({ r, onOpen }) {
  const id = r.application_id || r.student_id;
  const cell = (label, value, cls = "") => <div className="rounded-lg bg-slate-50 p-2"><div className="text-[10px] text-slate-500">{label}</div><div className={`font-black text-sm ${cls}`}>{value}</div></div>;
  return (
    <button type="button" onClick={() => onOpen(r)} className="text-right bg-white rounded-2xl border border-cream-200 p-4 card-shadow" data-testid={`overview-card-${id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="font-black text-slate-900 truncate">{r.student_name}</div><div className="text-xs text-teal-800 truncate">{r.opportunity_title || "—"}</div><div className="text-[11px] text-slate-500 truncate">{r.association_name || "—"}</div></div>
        <Badge meta={STATUS_META[r.status]} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 arabic-numerals">
        {cell("تقييم الجمعية", fmt(r.association_score, "/100"))}
        {cell("المهام", `${r.tasks_done}/${r.tasks_total}`)}
        {cell("متأخرة", r.tasks_overdue, r.tasks_overdue ? "text-rose-700" : "")}
        {cell("الساعات", r.hours)}
        {cell("الحضور", fmt(r.attendance_rate, "%"))}
        {cell("غياب", r.absent_days)}
      </div>
      <div className="mt-3"><Badge meta={CERT_META[r.certificate_status]} /><CertReasons r={r} testId={`cert-reasons-card-${id}`} /></div>
    </button>
  );
}
